/**
 * ProjectData.ts
 * Zod schemas for ProjectData validation
 *
 * CRITIQUE: Schéma strict pour éviter les erreurs runtime lors du chargement de projets
 * Remplace z.unknown() par une validation stricte qui vérifie au minimum:
 * - La présence de className (nécessaire pour Konva.Node.create())
 * - La structure des attrs (évite les crashes lors de la reconstruction)
 * - La structure récursive des children (valide toute la hiérarchie)
 *
 * Si vous changez la structure des objets Konva dans une future version,
 * ce schéma détectera les incompatibilités AVANT le crash runtime.
 */
import { z } from 'zod';

/**
 * Validateur pour les valeurs numériques qui rejette NaN et Infinity
 * CORRIGÉ PRO: Sécurité - empêche les valeurs malveillantes qui pourraient crasher le moteur de rendu
 */
const safeNumberSchema = z.number().refine(
  val => {
    // Rejeter NaN et Infinity
    if (!Number.isFinite(val)) {
      return false;
    }
    // Rejeter les valeurs extrêmes qui pourraient causer des problèmes de performance
    if (Math.abs(val) > 1e10) {
      return false;
    }
    return true;
  },
  { message: 'Les valeurs doivent être des nombres finis valides' }
);

/**
 * Validateur pour les coordonnées (x, y, width, height, etc.)
 * CORRIGÉ PRO: Validation stricte des valeurs géométriques
 */
const _coordinateSchema = safeNumberSchema.refine(
  val => Math.abs(val) <= 1e6, // Limite raisonnable pour les coordonnées
  { message: 'Les coordonnées doivent être dans une plage raisonnable' }
);

/**
 * Schéma récursif pour valider la structure des nœuds Konva
 * Basé sur la structure JSON retournée par Konva.Node.toJSON()
 * CORRIGÉ PRO: Validation stricte qui rejette les valeurs malveillantes
 */
// Type récursif pour les données de nœud Konva
type KonvaNodeData = {
  attrs?: Record<string, unknown>;
  className: string;
  children?: KonvaNodeData[];
  id?: string;
  name?: string;
};

const KonvaNodeDataSchema: z.ZodType<KonvaNodeData> = z.lazy(() =>
  z.object({
    attrs: z
      .record(z.unknown())
      .optional()
      .refine(
        attrs => {
          if (!attrs) return true;
          // Valider les valeurs numériques dans attrs
          for (const [key, value] of Object.entries(attrs)) {
            // Vérifier les propriétés géométriques critiques
            if (['x', 'y', 'width', 'height', 'rotation', 'scaleX', 'scaleY'].includes(key)) {
              if (typeof value === 'number' && !Number.isFinite(value)) {
                return false;
              }
            }
            // Rejeter les fonctions (sécurité)
            if (typeof value === 'function') {
              return false;
            }
            // Rejeter les objets avec des prototypes suspects
            if (typeof value === 'object' && value !== null && value.constructor !== Object) {
              // Permettre seulement les objets simples (pas de classes)
              if (value.constructor.name !== 'Object') {
                return false;
              }
            }
          }
          return true;
        },
        { message: 'Les attributs contiennent des valeurs invalides ou malveillantes' }
      ),
    className: z.string().min(1).max(100), // Limiter la longueur pour éviter les attaques
    children: z.array(KonvaNodeDataSchema).optional(),
    id: z.string().max(200).optional(), // Limiter la longueur
    name: z.string().max(200).optional(), // Limiter la longueur
  })
);

/**
 * Schéma pour valider les données du Stage Konva
 */
const KonvaStageDataSchema = z.object({
  attrs: z.record(z.unknown()).optional(),
  className: z.string(),
  children: z.array(KonvaNodeDataSchema).optional(),
});

/**
 * Schéma pour valider les données du canvas (format retourné par CanvasManager.toJSON())
 * Le schéma est flexible pour supporter à la fois les formats existants et nouveaux
 * CORRIGÉ PRO: Validation stricte des dimensions et valeurs numériques
 */
export const CanvasDataSchema = z.union([
  // Format structuré (nouveau format avec validation stricte)
  z.object({
    width: z
      .number()
      .positive()
      .max(1e6) // Limite raisonnable pour éviter les problèmes de mémoire
      .refine(val => Number.isFinite(val), { message: 'La largeur doit être un nombre fini' })
      .optional(),
    height: z
      .number()
      .positive()
      .max(1e6)
      .refine(val => Number.isFinite(val), { message: 'La hauteur doit être un nombre fini' })
      .optional(),
    zoomLevel: z
      .number()
      .min(0.01)
      .max(100)
      .refine(val => Number.isFinite(val), { message: 'Le zoom doit être un nombre fini' })
      .optional(),
    gridEnabled: z.boolean().optional(),
    backgroundColor: z.string().max(50).optional(), // Limiter la longueur
    stage: z.union([KonvaStageDataSchema, z.string().max(10 * 1024 * 1024)]).optional(), // Limiter à 10MB
  }),
  // Format legacy (pour rétrocompatibilité - mais avec validation de base)
  z.record(z.unknown()).refine(
    data => {
      // Valider au moins que ce n'est pas un objet malveillant
      const keys = Object.keys(data);
      if (keys.length > 10000) {
        return false; // Trop de clés = probablement malveillant
      }
      return true;
    },
    { message: 'Les données du canvas sont suspectes' }
  ),
]);

export const ProjectDataSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  data: CanvasDataSchema, // Schéma strict avec fallback pour rétrocompatibilité
  createdAt: z.number().positive(),
  updatedAt: z.number().positive(),
  version: z.string().optional(), // Version du format (ex: "1.0.0") - optionnel pour rétrocompatibilité
  project_type: z.enum(['template', 'project', 'template_editor']).optional(),
  /** Data-URL ou URL publique du thumbnail généré à la sauvegarde. */
  thumbnail_url: z.string().optional(),
});

export type ProjectData = z.infer<typeof ProjectDataSchema>;

/**
 * Validates and parses JSON string as ProjectData
 */
export function validateProjectData(
  jsonString: string
): { success: true; data: ProjectData } | { success: false; error: string } {
  try {
    const parsed = JSON.parse(jsonString);
    const validated = ProjectDataSchema.parse(parsed);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: `Validation error: ${error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
      };
    }
    if (error instanceof SyntaxError) {
      return { success: false, error: `Invalid JSON: ${error.message}` };
    }
    return {
      success: false,
      error: `Unknown error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Type guard pour valider les données du canvas
 * Vérifie que les données correspondent au schéma CanvasDataSchema
 */
export function isValidCanvasData(data: unknown): data is z.infer<typeof CanvasDataSchema> {
  try {
    CanvasDataSchema.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Valide et normalise les données du canvas
 * @param data - Données du canvas à valider
 * @returns Données validées ou erreur de validation
 */
export function validateCanvasData(
  data: unknown
): { success: true; data: z.infer<typeof CanvasDataSchema> } | { success: false; error: string } {
  try {
    const validated = CanvasDataSchema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: `Validation error: ${error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
      };
    }
    return {
      success: false,
      error: `Unknown error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}
